import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { showBanner } from '../../global-state/banner-slice'
import {
  webinarServices,
  WebinarFormData,
  WebinarDetailsResponse,
  ServiceResponse,
} from '../../services/webinar-service'
import { isValidImageType, createSlug } from '../../lib/utils/helpers'
import { WebinarsFormPresenter } from './webinars-form-presenter'

const MAX_FILE_SIZE_MB = 10

const getYoutubeThumbnailUrl = (youtubeVideoId: string) =>
  `https://img.youtube.com/vi/${youtubeVideoId.trim()}/hqdefault.jpg`

const defaultValues: WebinarFormData = {
  title: '',
  slug: '',
  description: '',
  youtube_video_id: '',
  thumbnail_url: '',
  published_at: new Date().toISOString().slice(0, 16),
  status: 'DRAFT',
}

const isWebinarDetailsResponse = (value: unknown): value is WebinarDetailsResponse =>
  typeof value === 'object' &&
  value !== null &&
  'data' in value &&
  typeof (value as WebinarDetailsResponse).data === 'object' &&
  (value as WebinarDetailsResponse).data !== null

const isServiceResponse = (value: unknown): value is ServiceResponse =>
  typeof value === 'object' && value !== null

function WebinarsFormContainer() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = Boolean(id)

  const webinarRef = useRef<WebinarFormData | null>(null)

  const form = useForm<WebinarFormData>({
    defaultValues,
    mode: 'onBlur',
  })

  const [thumbnailPreview, setThumbnailPreview] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!id) return
    const fetchWebinar = async () => {
      try {
        setIsLoading(true)
        const res = await webinarServices.getWebinarById(id)
        if (isWebinarDetailsResponse(res)) {
          const webinar = res.data
          webinarRef.current = webinar
          const publishedAt = webinar.published_at
            ? new Date(webinar.published_at).toISOString().slice(0, 16)
            : defaultValues.published_at
          form.reset({
            title: webinar.title ?? defaultValues.title,
            slug: webinar.slug ?? defaultValues.slug,
            description: webinar.description ?? defaultValues.description,
            youtube_video_id: webinar.youtube_video_id ?? defaultValues.youtube_video_id,
            thumbnail_url: webinar.thumbnail_url ?? defaultValues.thumbnail_url,
            published_at: publishedAt,
            status: webinar.status ?? defaultValues.status,
          })
          if (webinar.thumbnail_url) setThumbnailPreview(webinar.thumbnail_url)
        }
      } catch (error) {
        console.error(error)
        dispatch(showBanner({ text: 'Failed to fetch webinar', variant: 'error-banner' }))
      } finally {
        setIsLoading(false)
      }
    }
    fetchWebinar()
  }, [id, dispatch, form])

  const youtubeVideoId = form.watch('youtube_video_id')
  const thumbnailUrlValue = form.watch('thumbnail_url')

  const handleTitleBlur = () => {
    const title = form.getValues('title')
    const currentSlug = form.getValues('slug')
    if (title?.trim() && !currentSlug?.trim()) {
      form.setValue('slug', createSlug(title), {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }

  const displayedThumbnailPreview =
    thumbnailPreview ||
    (!thumbnailUrlValue && youtubeVideoId?.trim() ? getYoutubeThumbnailUrl(youtubeVideoId) : '')

  const handleFileUpload = async (file: File) => {
    if (!isValidImageType(file)) {
      dispatch(showBanner({ text: 'Only JPEG/JPG images are supported', variant: 'error-banner' }))
      return
    }
    const sizeInKB = Math.round(file.size / 1024)
    if (sizeInKB > MAX_FILE_SIZE_MB * 1024) {
      dispatch(
        showBanner({
          text: `File size must be under ${MAX_FILE_SIZE_MB} MB`,
          variant: 'error-banner',
        }),
      )
      return
    }

    const res = await webinarServices.uploadFile(file)
    if (isServiceResponse(res) && res.data?.key) {
      form.setValue('thumbnail_url', res.data.key, { shouldValidate: true, shouldDirty: true })
      setThumbnailPreview(URL.createObjectURL(file))
    } else {
      dispatch(showBanner({ text: 'Failed to upload image', variant: 'error-banner' }))
    }
  }

  const handleRemoveThumbnail = () => {
    form.setValue('thumbnail_url', null, { shouldValidate: true, shouldDirty: true })
    setThumbnailPreview('')
  }

  const buildUpdatePayload = (data: WebinarFormData): Partial<WebinarFormData> => {
    const payload: Partial<WebinarFormData> = {
      ...data,
      published_at: new Date(data.published_at).toISOString(),
      thumbnail_url: data.thumbnail_url || null,
    }

    if (!webinarRef.current) return payload

    const original = webinarRef.current
    const thumbnailRemoved = Boolean(original.thumbnail_url) && !data.thumbnail_url

    Object.entries(payload).forEach(([key, value]) => {
      if (thumbnailRemoved && key === 'thumbnail_url') return

      if (
        value === '' ||
        value === null ||
        value === undefined ||
        value === original[key as keyof WebinarFormData]
      ) {
        delete payload[key as keyof WebinarFormData]
      }
    })

    if (thumbnailRemoved) {
      payload.thumbnail_url = null
    }

    return payload
  }

  const onSubmit = async (data: WebinarFormData) => {
    setIsSubmitting(true)
    try {
      const slug = data.slug?.trim() || (data.title?.trim() ? createSlug(data.title) : '')
      const payload = {
        ...data,
        slug,
        published_at: new Date(data.published_at).toISOString(),
        thumbnail_url: data.thumbnail_url || null,
      }

      if (isEditMode && id) {
        const updatePayload = buildUpdatePayload({ ...data, slug })
        if (Object.keys(updatePayload).length === 0) {
          dispatch(showBanner({ text: 'No changes to update', variant: 'warning-banner' }))
          return
        }
        const res = await webinarServices.updateWebinar(id, updatePayload)
        if (isServiceResponse(res) && res.status === 200) {
          dispatch(showBanner({ text: 'Webinar updated successfully', variant: 'success-banner' }))
          navigate('/webinars')
        } else if (isServiceResponse(res)) {
          dispatch(
            showBanner({
              text: res.data?.message || 'Something went wrong! Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      } else {
        const res = await webinarServices.createWebinar(payload)
        if (isServiceResponse(res) && (res.status === 200 || res.status === 201)) {
          dispatch(showBanner({ text: 'Webinar created successfully', variant: 'success-banner' }))
          navigate('/webinars')
        } else if (isServiceResponse(res)) {
          dispatch(
            showBanner({
              text: res.data?.message || 'Something went wrong! Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <WebinarsFormPresenter
      form={form}
      isEditMode={isEditMode}
      isSubmitting={isSubmitting}
      isLoading={isLoading}
      thumbnailPreview={displayedThumbnailPreview}
      onSubmit={onSubmit}
      onFileUpload={handleFileUpload}
      onRemoveThumbnail={handleRemoveThumbnail}
      onTitleBlur={handleTitleBlur}
    />
  )
}

export { WebinarsFormContainer }
